// labelView
// i - if not neeed to show label (invisible)
// v - if ivisible label (if in filters)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import { Button, Icon, Input, Text } from '../../Elements/Elements';

import { validationSearch } from 'utils/validation';

import classes from './InputSearch.module.scss';

const InputSearch = ({
  value,
  onChange,
  placeholder,
  onClearSearch,
  className,
  label,
  labelView,
  debounceMode = true,
  disabled,
  validator,
  timeDebounce = 1000
}) => {
  const [userQuery, setUserQuery] = useState('');
  const [inputValue, setInputValue] = useState('');

  const updateQuery = () => {
    const res = { target: { value: userQuery ? userQuery : '' } };
    onChange(res ? res : { target: { value: userQuery ? userQuery : '' } });
  };

  // eslint-disable-next-line
  const delayedQuery = useCallback(debounce(updateQuery, timeDebounce), [
    userQuery
  ]);

  const onChangeDebounce = e => {
    if (
      !validator ? !validationSearch(e.target.value) : validator(e.target.value)
    ) {
      setInputValue(e.target.value);
      setUserQuery(e.target.value);
    }
  };

  const onClearDebounce = () => {
    setInputValue('');
    setUserQuery('');
  };

  const isInitialQueryRun = useRef(true);

  useEffect(() => {
    // Skip the initial (mount) run so InputSearch doesn't fire a spurious
    // debounced onChange('') ~1s after mount. That empty onChange would clobber
    // an externally-restored search value — e.g. the one re-applied when
    // switching DR region (P81-132768) — emptying the box after it populates.
    if (isInitialQueryRun.current) {
      isInitialQueryRun.current = false;
      return;
    }
    delayedQuery();
    return delayedQuery.cancel;
  }, [userQuery, delayedQuery]);

  useEffect(() => {
    setInputValue(value);
    if (debounceMode && value === '') {
      setUserQuery('');
    }
  }, [value, debounceMode]);

  return (
    <label
      className={cn(
        classes.filter,
        labelView === 'v' && classes.base,
        className
      )}
    >
      <Text
        className={cn(
          labelView !== 'v' ? classes.visually_hidden : classes.label
        )}
      >
        {label ? label : placeholder}
      </Text>
      <div className={cn(classes.search, disabled && classes.search_disabled)}>
        <Input
          input={{
            value: inputValue,
            onChange: disabled
              ? () => {}
              : debounceMode
              ? onChangeDebounce
              : onChange
          }}
          placeholder={placeholder}
          className={cn(
            classes.search_input,
            disabled && classes.search_input_disabled
          )}
        />
        {value ? (
          <Button
            className={classes.close}
            onClick={
              disabled
                ? () => {}
                : debounceMode
                ? () => {
                    onClearDebounce();
                    if (onClearSearch) onClearSearch();
                  }
                : onClearSearch
            }
            aria-label={`clear search`}
          >
            <Icon
              icon="closeCircle"
              className={cn(classes.close_icon, classes.close_icon_disabled)}
              size="16px"
            />
          </Button>
        ) : (
          <Icon
            icon={'search'}
            className={cn(
              classes.search_icon,
              disabled && classes.search_icon_disabled
            )}
            size="16px"
          />
        )}
      </div>
    </label>
  );
};

InputSearch.propTypes = {
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onClearSearch: PropTypes.func,
  value: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  labelView: PropTypes.oneOf(['v']),
  debounceMode: PropTypes.bool,
  disabled: PropTypes.bool,
  validator: PropTypes.func,
  timeDebounce: PropTypes.number
};

export default InputSearch;
